import type {
  IPublicClientApplication,
  AccountInfo,
} from "@azure/msal-browser";
import { API_BASE_URL } from "../constants";
import type { IDataItem } from "../models/IDataItem";
import { scopes } from "../authentication/PublicClientApplication";
import type { IUserPermissionItem } from "../models/IUserPermissionItem";
import type { IProjektItem } from "../models/IProjektItem";
import type { IDokumentItem } from "../models/IDokumentItem";
import type { IHistoryItem } from "../models/IHistoryItem";
import type { IKompetenzItem } from "../models/IKompetenzItem";
import type { IMeilensteinItem } from "../models/IMeilensteinItem";
import type { IWorkflowItem } from "../models/IWorkflowItem";
import type { IKachelmatrixItem } from "../models/IKachelmatrixItem";

export class DataService {
  private instance: IPublicClientApplication;
  private account: AccountInfo;

  constructor(instance: IPublicClientApplication, account: AccountInfo) {
    this.instance = instance;
    this.account = account;
  }

  async fetchWithAuth(url: string, options: RequestInit = {}) {
    const response = await this.instance.acquireTokenSilent({
      scopes,
      account: this.account,
    });
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${response.accessToken}`,
    };
    return fetch(url, { ...options, headers });
  }
  async getAppRoles(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/general/permission/rollen?appId=3`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDataItem[]>;
  }

  async deleteUserRole(itemId: number): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/general/permission/delete?itemId=${itemId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return true;
  }

  async addUserRole(item: IUserPermissionItem): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/general/permission/add`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    } else {
      return res.json() as Promise<boolean | string>;
    }
  }

  async getRolesAppPermissions(
    rollenId: string,
  ): Promise<IUserPermissionItem[]> {
    const url = `${API_BASE_URL}/fhelix/general/permission/rolesapppermissions?appId=3&rollenId=${rollenId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IUserPermissionItem[]>;
  }

  async getUserAppPermissions(
    userUpn: string,
    appId: string,
  ): Promise<IUserPermissionItem[]> {
    const url = `${API_BASE_URL}/fhelix/general/permission/userpermissions?userUpn=${userUpn}&appId=${appId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IUserPermissionItem[]>;
  }

  async getMitarbeiterSearchByUpn(upn: string): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/personal/mitarbeitersearchbyupn?search=${upn}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    } else {
      return res.json() as Promise<IDataItem[]>;
    }
  }

  async getRelevantePersonen(art: string): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/curriculum/relevantepersonen?art=${art}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    } else {
      return res.json() as Promise<IDataItem[]>;
    }
  }

  async getProjekteAuswahl(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/curriculum/projekteauswahl`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    } else {
      return res.json() as Promise<IDataItem[]>;
    }
  }

  async getProjekteRasterAuswahl(projektId: number): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/curriculum/projekteRasterauswahl?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    } else {
      return res.json() as Promise<IDataItem[]>;
    }
  }

  async getStudiengaengeAuswahl(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/curriculum/studiengaengeauswahl`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    } else {
      return res.json() as Promise<IDataItem[]>;
    }
  }

  async getProjektListe(
    search: string,
    typ: string,
    art: string,
    status: string,
  ): Promise<IProjektItem[]> {
    const url = `${API_BASE_URL}/fhelix/curriculum/projektliste?search=${encodeURIComponent(search)}&typ=${typ}&art=${art}&status=${status}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IProjektItem[]>;
  }

  async getProjektDokumente(projektId: number): Promise<IDokumentItem[]> {
    const url = `${API_BASE_URL}/fhelix/curriculum/projektdokumente?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IDokumentItem[]>;
  }

  async getDokumentKategorien(): Promise<IDataItem[]> {
    const url = `${API_BASE_URL}/fhelix/curriculum/dokumentkategorien`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    } else {
      return res.json() as Promise<IDataItem[]>;
    }
  }

  uploadProjektDokument = async (
    projektId: number,
    fileName: string,
    extension: string,
    file: Blob,
    kategorieId: string,
  ): Promise<string> => {
    const url = `${API_BASE_URL}/fhelix/curriculum/uploadprojektdokument?projektId=${projektId}&fileName=${fileName}&extension=${extension}&kategorieId=${kategorieId}`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      body: file,
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<string>;
  };

  async deleteProjektDokument(dokumentid: string): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/curriculum/deleteprojektdokument?dokumentid=${dokumentid}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  async downloadDocument(dokumentid: string): Promise<Blob> {
    const url = `${API_BASE_URL}/fhelix/curriculum/downloaddocument?dokumentid=${dokumentid}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.blob() as Promise<Blob>;
  }

  async getProjekt(projektId: number): Promise<IProjektItem> {
    const url = `${API_BASE_URL}/fhelix/curriculum/projektbyid?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IProjektItem>;
  }

  async getProjektWithTaskGuid(taskGuid: string): Promise<IProjektItem> {
    const url = `${API_BASE_URL}/fhelix/curriculum/projektbytaskguid?taskGuid=${taskGuid}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IProjektItem>;
  }

  async addUpdateProjekt(item: IProjektItem): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/curriculum/addupdateprojekt`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean | string>;
  }

  async getProjektHistory(
    projektId: number,
    search: string,
    typ: string,
    aktion: string,
  ): Promise<IHistoryItem[]> {
    const url = `${API_BASE_URL}/fhelix/curriculum/projekthistory?projektId=${projektId}&search=${encodeURIComponent(search)}&typ=${typ}&aktion=${aktion}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IHistoryItem[]>;
  }

  async getProjektKompetenzen(projektId: number): Promise<IKompetenzItem[]> {
    const url = `${API_BASE_URL}/fhelix/curriculum/projektkompetenzen?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IKompetenzItem[]>;
  }

  async getProjektKompetenzenXlsx(projektId: number): Promise<string> {
    const url = `${API_BASE_URL}/fhelix/curriculum/projektkompetenzenxlsx?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.text() as Promise<string>;
  }

  async createKompetenzraster(
    projektId: number,
    uebernahmeProjektId: number,
  ): Promise<boolean> {
    const url = `${API_BASE_URL}/fhelix/curriculum/createkompetenzraster?projektId=${projektId}&uebernahmeProjektId=${uebernahmeProjektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean>;
  }

  async updateKompetenz(item: IKompetenzItem): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/curriculum/updatekompetenzitem`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean | string>;
  }

  async getProjektMeilensteine(projektId: number): Promise<IMeilensteinItem[]> {
    const url = `${API_BASE_URL}/fhelix/curriculum/projektmeilensteine?projektId=${projektId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IMeilensteinItem[]>;
  }

  async updateMeilenstein(item: IMeilensteinItem): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/curriculum/updatemeilensteinplan`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean | string>;
  }

  async getModuleKachelmatrix(
    projektId: number,
    version: string,
  ): Promise<IKachelmatrixItem[]> {
    const url = `${API_BASE_URL}/fhelix/curriculum/modulekachelmatrix?projektId=${projektId}&version=${version}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IKachelmatrixItem[]>;
  }

  async getKachelmatrixItem(modulId: number): Promise<IKachelmatrixItem> {
    const url = `${API_BASE_URL}/fhelix/curriculum/modulkachelmatrix?modulId=${modulId}`;
    const res = await this.fetchWithAuth(url, {
      method: "GET",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<IKachelmatrixItem>;
  }

  async createKachelmatrixItem(
    item: IKachelmatrixItem,
  ): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/curriculum/createmodulkachelmatrixitem`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean | string>;
  }

  async updateKachelmatrixItem(
    item: IKachelmatrixItem,
  ): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/curriculum/updatemodulkachelmatrixitem`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean | string>;
  }

  async deleteKachelmatrixItem(modulId: number): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/curriculum/deletemodulkachelmatrixitem?modulId=${modulId}`;
    const res = await this.fetchWithAuth(url, {
      method: "DELETE",
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean | string>;
  }

  async deleteKachelmatrix(modulId: number): Promise<boolean | string> {
    return this.deleteKachelmatrixItem(modulId);
  }

  async startWorkflow(item: IWorkflowItem): Promise<boolean | string> {
    const url = `${API_BASE_URL}/fhelix/curriculum/startworkflow`;
    const res = await this.fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error get data: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res.json() as Promise<boolean | string>;
  }
}
